import { useMemoizedFn } from 'ahooks';
import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Link, Select, Table } from 'tdesign-react';
import useTable from '@/hooks/useTable';
import { queryRecordList } from '@/services/execRecordService';
import {
  isVoid,
  renderDateTime,
  renderOptions,
  renderStatusOptions,
  renderTimeUnit,
  toTsOptions,
} from '@/utils/common';
import { processStatusOptions, jobOptions, executeResultOptions } from '@/utils/constant';
import BuriedWrapper from '@/components/BuriedWrapper';

const { FormItem } = Form;

export default function RecordList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stencilType = isVoid(searchParams.get('stencilType'))
    ? null
    : Number(searchParams.get('stencilType'));

  /**
   * @type {import('tdesign-react/lib/table/type').BaseTableCol}
   */
  const columns = [
    {
      colKey: 'id',
      title: '任务ID',
      width: 80,
      fixed: 'left',
      cell: ({ row }) => (
        <BuriedWrapper eventId='EXECUTIVE_DETAIL_RECORD_DETAIL'>
          <Link
            onClick={() =>
              navigate(
                `/maintenance/executive/record/detail?id=${row.id}&clusterId=${row.clusterId}`,
              )
            }
            theme='primary'
          >
            {row.id}
          </Link>
        </BuriedWrapper>
      ),
    },
    {
      colKey: 'stencilType',
      title: '任务类型',
      width: 110,
      options: jobOptions,
      cell: renderOptions,
    },
    {
      colKey: 'taskName',
      title: '任务名称',
      width: 160,
    },
    {
      colKey: 'clusterName',
      title: '所属集群',
      width: 210,
    },
    {
      colKey: 'taskNote',
      title: '任务描述',
      width: 200,
    },
    {
      colKey: 'status',
      title: '状态',
      options: processStatusOptions,
      cell: renderStatusOptions,
      width: 100,
    },
    {
      colKey: 'execResultCode',
      title: '结果',
      options: executeResultOptions,
      cell: renderStatusOptions,
      width: 120,
    },
    {
      colKey: 'beginTime',
      title: '开始时间',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'endTime',
      title: '完成时间',
      cell: renderDateTime,
      width: 180,
    },
    {
      colKey: 'execUseTime',
      title: '执行耗时',
      cell: renderTimeUnit,
      width: 100,
    },
    {
      title: '操作人',
      colKey: 'createBy',
      width: 150,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { data } = await queryRecordList(params);
    setData(
      data.items.map((item) => ({
        ...item,
        execResultCode: Number(item.execResultCode),
      })),
      data.count,
    );
  });

  const { header, config, setData, doSearch } = useTable({
    columns,
    search: (
      <>
        <FormItem name='status'>
          <Select options={toTsOptions(processStatusOptions)} clearable placeholder='状态' />
        </FormItem>
        <FormItem name='stencilType' initialData={stencilType}>
          <Select options={toTsOptions(jobOptions)} clearable placeholder='任务类型' />
        </FormItem>
        <FormItem name='execResultCode'>
          <Select options={toTsOptions(executeResultOptions)} clearable placeholder='结果' />
        </FormItem>
      </>
    ),
    remote: true,
    onSearch: loadData,
  });

  useEffect(() => {
    doSearch({ stencilType });
  }, [doSearch, stencilType]);

  return (
    <>
      {header}
      <Table {...config} rowKey='id' />
    </>
  );
}
